(() => {
  /* Guía de tallas: modal simple, sin dependencias del carrito ni del
     variant-picker nativo. Abre con [data-naylumi-size-guide-open], cierra
     con el botón, el backdrop o Escape. Bloquea el scroll del body mientras
     está abierto y lo libera siempre al cerrar (sin fugas si el modal se
     cierra por cualquiera de las tres vías). */
  const initSizeGuide = (root) => {
    const modal = root.querySelector('[data-naylumi-size-guide]');
    if (!modal || modal.dataset.naylumiSizeGuideBound) return;
    modal.dataset.naylumiSizeGuideBound = 'true';

    const openTriggers = root.querySelectorAll('[data-naylumi-size-guide-open]');
    const closeTriggers = modal.querySelectorAll('[data-naylumi-size-guide-close]');
    let lastFocused = null;

    const openModal = () => {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('[data-naylumi-size-guide-close]');
      closeBtn?.focus();
      document.addEventListener('keydown', onKeydown);
    };

    const closeModal = () => {
      modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    openTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openModal();
      });
    });

    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', closeModal);
    });
  };

  /* Favoritos: manejados por el sistema único compartido en
     assets/naylumi-favorites.js (botón con [data-naylumi-fav-toggle],
     cargado desde el header, presente en toda página). */

  /* Botón "Agregar al carrito" como toggle por variante.
     Regla clave: solo interceptamos el click cuando YA sabemos (por el
     último estado sincronizado) que la variante seleccionada está en el
     carrito. Si no lo está, el click NO se toca en absoluto y el flujo
     nativo de Shopify (product-form-component / add-to-cart-component)
     corre exactamente igual que siempre: animación, apertura del drawer,
     acumulación de cantidad, etc. Esto evita tener que reimplementar o
     re-disparar el submit nativo. */
  const initCartToggle = (root) => {
    root.querySelectorAll('.buy-buttons-block').forEach((block) => {
      const productForm = block.querySelector('product-form-component');
      if (!productForm || productForm.dataset.naylumiCartToggleBound) return;
      productForm.dataset.naylumiCartToggleBound = 'true';

      let isRemoving = false;
      let cachedInCart = false;

      const getVariantId = () => {
        const input = productForm.querySelector('input[name="id"]');
        return input && input.value ? input.value : null;
      };

      const getButtons = () => Array.from(productForm.querySelectorAll('.add-to-cart-button'));

      const setButtonsState = (inCart) => {
        cachedInCart = inCart;
        getButtons().forEach((button) => {
          button.classList.toggle('naylumi-added-to-cart', inCart);
        });
      };

      const fetchCart = () =>
        fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
          (response) => {
            if (!response.ok) throw new Error('cart fetch failed');
            return response.json();
          }
        );

      // Aplica una lista de items de carrito ya obtenida (evita un fetch
      // extra cuando el evento nativo ya la trae, igual que hace
      // ProductFormComponent.#updateCartQuantity en product-form.js).
      const applyCartItems = (items) => {
        const variantId = getVariantId();
        if (!variantId) return;
        const inCart = items.some((item) => String(item.variant_id) === String(variantId));
        setButtonsState(inCart);
      };

      // Recalcula el estado del botón para la variante ACTUALMENTE
      // seleccionada. Se llama al cargar, al cambiar de variante y cuando
      // el carrito cambia desde otro lugar sin traer detalle de items.
      const syncState = () => {
        const variantId = getVariantId();
        if (!variantId) return;
        fetchCart()
          .then((cart) => {
            if (getVariantId() !== variantId) return; // la variante ya cambió
            applyCartItems(cart.items);
          })
          .catch(() => {});
      };

      // Quita del carrito la línea de esta variante usando su `key`
      // (recomendado por Shopify sobre variant_id cuando puede haber
      // varias líneas). Reutiliza el método nativo onLineItemRemove del
      // cart-items-component cuando está disponible; si no, cae a
      // /cart/change.js pasando el key como id.
      const removeVariantFromCart = (variantId, button) => {
        fetchCart()
          .then((cart) => {
            const cartItem = cart.items.find((item) => String(item.variant_id) === String(variantId));
            if (!cartItem) return null;

            const cartItemsComponent = document.querySelector('cart-items-component');
            const rows = Array.from(document.querySelectorAll('[ref="cartItemRows[]"]'));
            const row = rows.find((candidate) => candidate.dataset.key === cartItem.key);

            if (cartItemsComponent && row && typeof cartItemsComponent.onLineItemRemove === 'function') {
              const line = rows.indexOf(row) + 1;
              cartItemsComponent.onLineItemRemove(line);
              return null;
            }

            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: cartItem.key, quantity: 0 }),
            }).then(() => {
              if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
                cartItemsComponent.fetchCartData();
              }
            });
          })
          .catch(() => {})
          .finally(() => {
            isRemoving = false;
            if (button) button.disabled = false;
            syncState();
          });
      };

      const handleCapturedClick = (event) => {
        if (!cachedInCart) return; // no está en el carrito: no tocar el click nativo

        const button = event.target.closest('.add-to-cart-button');
        if (!button || !productForm.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();

        // El binding nativo on:click del tema se resuelve con un listener en
        // capture a nivel document (ver assets/component.js), que está más
        // arriba en el árbol que este listener y por lo tanto ya corrió
        // antes de llegar aquí: el check/animación de "agregado" ya se
        // disparó. Lo deshacemos en el mismo tick, antes de pintar, para
        // que una acción de QUITAR no muestre el check nativo de agregado.
        button.removeAttribute('data-added');
        document.querySelectorAll('fly-to-cart').forEach((el) => el.remove());

        if (isRemoving) return; // protección contra doble click
        isRemoving = true;
        button.disabled = true;

        const variantId = getVariantId();
        if (!variantId) {
          isRemoving = false;
          button.disabled = false;
          return;
        }

        removeVariantFromCart(variantId, button);
      };

      productForm.addEventListener('click', handleCapturedClick, { capture: true });

      const sectionRoot = productForm.closest('.shopify-section, dialog, product-card') || productForm;
      sectionRoot.addEventListener('shopify:product:select', (event) => {
        const promise = event.promise;
        if (promise && typeof promise.then === 'function') {
          promise.then(syncState).catch(syncState);
        } else {
          syncState();
        }
      });

      // El evento se dispara ANTES de que termine el fetch real a Shopify
      // (trae su propia `promise` que resuelve al terminar); hay que
      // esperarla o el /cart.js que pedimos acá llega con datos viejos.
      // Si la promesa ya trae detail.items (como en un add normal) los
      // usamos directo, igual que ProductFormComponent#onCartUpdate, para
      // no esperar un segundo round-trip a /cart.js.
      document.addEventListener('shopify:cart:lines-update', (event) => {
        const promise = event.promise;
        if (promise && typeof promise.then === 'function') {
          promise
            .then((result) => {
              if (result?.detail?.items) {
                applyCartItems(result.detail.items);
              } else {
                syncState();
              }
            })
            .catch(syncState);
        } else {
          syncState();
        }
      });

      syncState();
    });
  };

  const init = () => {
    document.querySelectorAll('.naylumi-product').forEach((root) => {
      initSizeGuide(root);
      initCartToggle(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target.matches && target.matches('.naylumi-product')) {
      initSizeGuide(target);
      initCartToggle(target);
    }
    target.querySelectorAll('.naylumi-product').forEach((root) => {
      initSizeGuide(root);
      initCartToggle(root);
    });
  });
})();
